<template>
  <div 
    class="app-card" 
    :class="[
      `app-card-${variant}`,
      { 
        'app-card-hover': hover,
        'app-card-flat': flat,
        'app-card-bordered': bordered && !flat
      }
    ]"
    role="region"
    aria-label="card"
  >
    <div v-if="$slots.image" class="app-card-image">
      <slot name="image"></slot>
    </div>
    
    <div v-if="$slots.header" class="app-card-header">
      <slot name="header"></slot>
    </div>
    
    <div class="app-card-body">
      <h3 v-if="title" class="app-card-title">{{ title }}</h3>
      <p v-if="subtitle" class="app-card-subtitle">{{ subtitle }}</p>
      <slot></slot>
    </div>
    
    <div v-if="$slots.footer" class="app-card-footer">
      <slot name="footer"></slot>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'AppCard',
  props: {
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    variant: {
      type: String,
      default: 'default',
      validator: value => ['default', 'primary', 'success', 'warning', 'danger', 'info'].includes(value)
    },
    hover: {
      type: Boolean,
      default: false
    },
    flat: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: false
    }
  }
});
</script>

<style scoped>
.app-card {
  display: flex;
  flex-direction: column;
  background-color: var(--surface-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  width: 100%;
  height: 100%;
  transition: all var(--transition-base) var(--transition-timing);
}

.app-card:not(.app-card-flat) {
  box-shadow: var(--box-shadow-sm);
}

.app-card-hover:not(.app-card-flat):hover {
  transform: translateY(-4px);
  box-shadow: var(--box-shadow-md);
}

.app-card-bordered {
  border: 1px solid var(--border-color);
}

.app-card-image {
  width: 100%;
  position: relative;
  overflow: hidden;
}

.app-card-image img {
  width: 100%;
  height: auto;
  display: block;
  transition: transform var(--transition-base) var(--transition-timing);
}

.app-card-hover .app-card-image img:hover {
  transform: scale(1.05);
}

.app-card-header {
  padding: var(--spacing-md) var(--spacing-md) 0;
  border-bottom: none;
}

.app-card-body {
  padding: var(--spacing-md);
  flex: 1 1 auto;
}

.app-card-footer {
  padding: 0 var(--spacing-md) var(--spacing-md);
  background-color: transparent;
  border-top: none;
}

.app-card-title {
  margin-top: 0;
  margin-bottom: var(--spacing-sm);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--text-color);
  line-height: var(--line-height-tight);
}

.app-card-subtitle {
  margin-top: 0;
  margin-bottom: var(--spacing-md);
  color: var(--text-light);
  font-size: var(--font-size-base);
}

/* Card variants */
.app-card-primary {
  border-top: 4px solid var(--primary-color);
}

.app-card-success {
  border-top: 4px solid var(--success-color);
}

.app-card-warning {
  border-top: 4px solid var(--warning-color);
}

.app-card-danger {
  border-top: 4px solid var(--danger-color);
}

.app-card-info {
  border-top: 4px solid var(--info-color);
}

/* Card with only image and content overlay */
.app-card-overlay {
  position: relative;
}

.app-card-overlay .app-card-image {
  height: 100%;
}

.app-card-overlay .app-card-image img {
  height: 100%;
  object-fit: cover;
}

.app-card-overlay .app-card-body {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0) 100%);
  color: white;
}

.app-card-overlay .app-card-title,
.app-card-overlay .app-card-subtitle {
  color: white;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .app-card-body {
    padding: 1rem;
  }
  
  .app-card-header,
  .app-card-footer {
    padding: 0.75rem 1rem;
  }
  
  .app-card-title {
    font-size: 1.125rem;
  }
}

/* Loading skeleton states */
.app-card-skeleton-line {
  height: 1rem;
  margin-bottom: 0.5rem;
  border-radius: var(--border-radius-sm, 0.25rem);
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-card-skeleton-line {
    animation: none;
  }
  
  .app-card, 
  .app-card-hover:hover {
    transition: none;
    transform: none;
  }
}
</style> 